/**
 * Job, job event, cost ledger row, API error envelope and proposal contracts
 * (ARCHITECTURE.md §4.5, §4.7, §4.8).
 */
import { z } from 'zod';
import { CharacterBody } from './character';
import { AnnotationKind, Id, IsoDateTime, NonNegInt, PosInt } from './common';
import { ObjectBody } from './object';
import { StageBody } from './stage';

// ---------------------------------------------------------------------------
// 4.7 Jobs
// ---------------------------------------------------------------------------

export const JobType = z.enum([
  'index.full',
  'index.incremental',
  'media.warm',
  'media.thumb',
  'media.sheet',
  'media.raster',
  'audio.probe',
  'cache.sweep',
  'annotate.heuristic',
  'ai.annotate_stage',
  'ai.annotate_character',
  'ai.annotate_sprite',
  'ai.annotate_objects',
  'ai.story',
  'ai.story_cast',
  'tts.bake',
  'tts.warm',
  'export.bundle',
  'export.video',
  'import.overrides',
  'ai.health',
  'ai.action_author',
]);
export type JobType = z.infer<typeof JobType>;

export const JobLane = z.enum(['io', 'cpu', 'ai_interactive', 'ai_bulk']);
export type JobLane = z.infer<typeof JobLane>;

/** There is no `paused` status: a pause is reported through `progress.phase` (§4.7). */
export const JobStatus = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
export type JobStatus = z.infer<typeof JobStatus>;

export const PausedFor = z.enum(['interactive', 'usage_window', 'rate_limit']);
export type PausedFor = z.infer<typeof PausedFor>;

/** Free-form progress data with the well-known keys typed. Open by contract (`[k: string]: unknown`). */
export const JobProgressData = z.looseObject({
  ai_model: z.string().optional(),
  ai_provider: z.string().optional(),
  ai_models: z.array(z.object({ model: z.string(), provider: z.string() })).optional(),
  cost_usd: z.number().min(0).optional(),
  paused_until: z.string().optional(),
  paused_for: PausedFor.optional(),
  eta_s: z.number().min(0).optional(),
});
export type JobProgressData = z.infer<typeof JobProgressData>;

export const JobProgress = z.strictObject({
  /** Technical phase name (`ai.story` → catalog → outline → scenes → …). */
  phase: z.string().nullable(),
  /** 0..1 */
  pct: z.number().min(0).max(1).nullable(),
  /** PARENT-FACING copy (phases.py). */
  msg: z.string().nullable(),
  data: JobProgressData.optional(),
});
export type JobProgress = z.infer<typeof JobProgress>;

export const Job = z
  .strictObject({
    id: PosInt,
    type: JobType,
    lane: JobLane,
    status: JobStatus,
    /** 1 interactive (ai.story, its tts.bake, "now"), 3 batch, 5 default, 7 background. */
    priority: z.int(),
    params: z.record(z.string(), z.unknown()),
    result: z.record(z.string(), z.unknown()).optional(),
    error: z.string().optional(),
    progress: JobProgress,
    attempts: NonNegInt,
    max_attempts: PosInt,
    created_at: IsoDateTime,
    started_at: IsoDateTime.optional(),
    finished_at: IsoDateTime.optional(),
    heartbeat_at: IsoDateTime.optional(),
    run_after: IsoDateTime.optional(),
    cancel_requested: z.boolean(),
    event_seq: NonNegInt.optional(),
    parent_job_id: PosInt.optional(),
    created_by: z.string().optional(),
    budget_usd: z.number().min(0).optional(),
    cost_usd: z.number().min(0).optional(),
    children: z.strictObject({ total: NonNegInt, done: NonNegInt, failed: NonNegInt }).optional(),
  })
  .meta({ id: 'Job', title: 'Job', description: 'A queued, running or finished background job.' });
export type Job = z.infer<typeof Job>;

export const JobEventKind = z.enum(['queued', 'started', 'progress', 'log', 'succeeded', 'failed', 'cancelled']);
export type JobEventKind = z.infer<typeof JobEventKind>;

export const JobEvent = z
  .strictObject({
    job_id: PosInt,
    seq: NonNegInt,
    ts: IsoDateTime,
    kind: JobEventKind,
    data: z.looseObject({
      phase: z.string().optional(),
      pct: z.number().min(0).max(1).optional(),
      msg: z.string().optional(),
      cost_usd: z.number().min(0).optional(),
      eta_s: z.number().min(0).optional(),
    }),
  })
  .meta({ id: 'JobEvent', title: 'Job event', description: 'One SSE event of a job (id = seq).' });
export type JobEvent = z.infer<typeof JobEvent>;

// ---------------------------------------------------------------------------
// 4.8 Cost ledger row
// ---------------------------------------------------------------------------

export const AiCallKind = z.enum(['vision', 'story', 'cheap', 'health']);
export type AiCallKind = z.infer<typeof AiCallKind>;

export const AiPurpose = z.enum([
  'annotate_stage',
  'annotate_character',
  'annotate_sprite',
  'annotate_objects',
  'outline',
  'story_cast',
  'scene',
  'repair',
  'judge',
  'ping',
]);
export type AiPurpose = z.infer<typeof AiPurpose>;

export const AiErrorKind = z.enum([
  'unavailable',
  'timeout',
  'auth',
  'budget',
  'usage_window',
  'rate_limit',
  'schema',
  'turns',
  'exec',
  'refusal',
]);
export type AiErrorKind = z.infer<typeof AiErrorKind>;

export const CostLedgerRow = z
  .strictObject({
    id: PosInt,
    ts: IsoDateTime,
    kind: AiCallKind,
    purpose: AiPurpose,
    job_id: PosInt.optional(),
    asset_uid: z.string().optional(),
    story_uid: z.string().optional(),
    user_id: z.string().optional(),
    model: z.string().optional(),
    effort: z.string().optional(),
    attempt: PosInt,
    /** success | error_max_turns | error_max_budget_usd | … | timeout | spawn_error | cancelled */
    subtype: z.string().optional(),
    error_kind: AiErrorKind.optional(),
    is_error: z.boolean(),
    /** May exceed max_turns on a success (structured output uses a turn). */
    num_turns: NonNegInt.optional(),
    duration_ms: NonNegInt.optional(),
    duration_api_ms: NonNegInt.optional(),
    input_tokens: NonNegInt.optional(),
    output_tokens: NonNegInt.optional(),
    cache_read_tokens: NonNegInt.optional(),
    cache_write_tokens: NonNegInt.optional(),
    /** CLI `total_cost_usd`: a list-price ESTIMATE, never a bill. */
    cost_usd: z.number().min(0),
    /** 'unknown' = killed/timed-out call with no envelope; excluded from averages. */
    cost_basis: z.enum(['list', 'unknown', 'mock']),
    billing: z.enum(['api', 'subscription', 'mock', 'gemini']),
    /** sha1 of the compact --json-schema. */
    schema_hash: z.string().optional(),
    session_id: z.string().optional(),
  })
  .meta({ id: 'CostLedgerRow', title: 'Cost ledger row', description: 'One CLI invocation, also on failure.' });
export type CostLedgerRow = z.infer<typeof CostLedgerRow>;

// ---------------------------------------------------------------------------
// 4.8 API error envelope
// ---------------------------------------------------------------------------

/** Well-known error codes (§4.8 table). `code` stays an open string so a new code never breaks the client. */
export const API_ERROR_CODES = [
  'bad_request',
  'not_found',
  'conflict',
  'validation_error',
  'not_published',
  'ai_rate_limited',
  'ai_unavailable',
  's3_unavailable',
  'tts_unavailable',
  'disk_full',
  'internal',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const ApiError = z
  .strictObject({
    error: z.strictObject({
      code: z.string().min(1),
      message: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    }),
  })
  .meta({ id: 'ApiError', title: 'API error', description: 'Error envelope returned by every failing route.' });
export type ApiError = z.infer<typeof ApiError>;

// ---------------------------------------------------------------------------
// 4.5 Proposal
// ---------------------------------------------------------------------------

export const ProposalSource = z.enum(['heuristic', 'ai', 'mock']);
export type ProposalSource = z.infer<typeof ProposalSource>;

export const ProposalStatus = z.enum(['pending', 'accepted', 'partial', 'rejected', 'superseded']);
export type ProposalStatus = z.infer<typeof ProposalStatus>;

/** Computed by the server on GET for the review UI. */
export const ProposalDiff = z
  .strictObject({
    added: z.array(z.string()),
    changed: z.array(z.strictObject({ id: z.string(), iou: z.number().min(0).max(1).optional(), dist: z.number().min(0).optional() })),
    unchanged: z.array(z.string()),
    removed: z.array(z.string()),
    locked_skipped: z.array(z.string()),
  })
  .meta({ id: 'ProposalDiff' });
export type ProposalDiff = z.infer<typeof ProposalDiff>;

/** The three body shapes are strict and structurally disjoint, so a plain union is unambiguous. */
export const ProposalBody = z.union([StageBody, CharacterBody, ObjectBody]);
export type ProposalBody = z.infer<typeof ProposalBody>;

const ProposalShape = z.strictObject({
  id: PosInt,
  asset_uid: Id,
  kind: AnnotationKind,
  source: ProposalSource,
  model: z.string().optional(),
  prompt_version: z.string().optional(),
  vocab_rev: NonNegInt,
  cost_usd: z.number().min(0),
  duration_ms: NonNegInt.optional(),
  /** Draft rev the proposal was computed against. */
  base_rev: NonNegInt.optional(),
  /** Every feature carries provenance (source ai/heuristic, confidence). */
  body: ProposalBody,
  notes: z.string().optional(),
  /** 'coord_frame_suspect' | 'unknown_kind:<k>' | 'low_confidence' | 'schema_retry' */
  flags: z.array(z.string()),
  status: ProposalStatus,
  created_at: IsoDateTime,
  resolved_at: IsoDateTime.optional(),
  resolved_by: z.string().optional(),
  diff: ProposalDiff.optional(),
});

export const Proposal = ProposalShape.meta({
  id: 'Proposal',
  title: 'Proposal',
  description: 'AI / heuristic annotation output awaiting review.',
});
export type Proposal = z.infer<typeof Proposal>;

/** `GET /api/proposals` list items: a Proposal without its body. */
export const ProposalSummary = ProposalShape.omit({ body: true, diff: true });
export type ProposalSummary = z.infer<typeof ProposalSummary>;
